import { RefreshCw, Inbox } from 'lucide-react';
import type { ReactNode } from 'react';

export interface QueueColumn<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
}

interface QueueTableProps<T> {
  columns: QueueColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  isLoading: boolean;
  /** Fired when a row is clicked (e.g. opens the detail drawer). */
  onRowClick?: (row: T) => void;
  /**
   * DM-5806: with onRowClick, renders a named Open button in a trailing cell.
   * A native button is what makes the row's action reachable by keyboard AND
   * announced by a screen reader; a focusable <tr> is still read as a row.
   */
  rowActionLabel?: (row: T) => string;
  emptyTitle: string;
  emptyHint?: string;
  /** Cursor pagination wiring from useInfiniteQuery. */
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}

const SKELETON_ROWS = 5;

/**
 * Shared queue table: a real <table> with cursor-paginated load-more —
 * queue endpoints are server-paginated, never fetch-all.
 */
export default function QueueTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  onRowClick,
  rowActionLabel,
  emptyTitle,
  emptyHint,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: QueueTableProps<T>) {
  const hasRowAction = onRowClick !== undefined && rowActionLabel !== undefined;

  if (isLoading) {
    return (
      <div className="glass-card p-12 text-center">
        <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
        <p className="text-gray-text">Loading queue…</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="glass-card p-12 text-center">
        <Inbox className="w-12 h-12 text-gray-text mx-auto mb-4" />
        <h3 className="text-white font-medium mb-1">{emptyTitle}</h3>
        {emptyHint && <p className="text-sm text-gray-text">{emptyHint}</p>}
      </div>
    );
  }

  return (
    <div className="glass-card overflow-hidden">
      <div className="overflow-x-auto custom-scrollbar">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-border">
              {columns.map(column => (
                <th
                  key={column.key}
                  scope="col"
                  className="text-left px-4 py-3 text-sm font-medium text-gray-text whitespace-nowrap"
                >
                  {column.header}
                </th>
              ))}
              {hasRowAction && (
                <th scope="col" className="px-4 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-b border-gray-border/50 ${
                  onRowClick ? 'cursor-pointer hover:bg-gray-800/30' : 'hover:bg-gray-800/20'
                }`}
              >
                {columns.map(column => (
                  <td key={column.key} className="px-4 py-3 text-sm text-white align-top">
                    {column.render(row)}
                  </td>
                ))}
                {hasRowAction && (
                  <td className="px-4 py-3 text-right align-top">
                    <button
                      type="button"
                      aria-label={rowActionLabel(row)}
                      className="btn-secondary text-xs"
                      onClick={event => {
                        event.stopPropagation();
                        onRowClick(row);
                      }}
                    >
                      Open
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {isFetchingNextPage &&
              Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <tr key={`skeleton-${index}`} className="border-b border-gray-border/50">
                  {columns.map(column => (
                    <td key={column.key} className="px-4 py-3">
                      <div className="h-4 bg-dark-lighter rounded animate-pulse" />
                    </td>
                  ))}
                  {hasRowAction && <td className="px-4 py-3" />}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {hasNextPage && onLoadMore && (
        <div className="p-4 border-t border-gray-border text-center">
          <button
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            className="btn-secondary disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
